/**
 * Pure helpers for the editable statement review step. The reviewed draft is
 * what the API persists and what Apply uses verbatim (the AI is never re-run).
 */

export interface ReviewLine {
  description?: string;
  category?: string;
  amount: number | string;
}

export interface ReviewDraft {
  month: number | null;   // 1-12
  year: number | null;    // e.g. 2026
  income: ReviewLine[];
  expenses: ReviewLine[];
  owner_distributions: ReviewLine[];
}

export const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

function toNumber(value: any): number {
  if (typeof value === 'number') {
    return value;
  }
  const n = parseFloat(String(value == null ? '' : value).replace(/[$,\s()]/g, ''));
  return isFinite(n) ? n : NaN;
}

/** Build an editable draft from an AI result or a saved analysis. */
export function toReviewDraft(analysis: any): ReviewDraft {
  const source = analysis || {};
  const match = /^(\d{4})-(\d{1,2})/.exec(String(source.statement_date || ''));
  const lines = (list: any[], withCategory: boolean): ReviewLine[] =>
    (Array.isArray(list) ? list : []).map(item => {
      const line: ReviewLine = {
        description: (item && item.description) || '',
        amount: isNaN(toNumber(item && item.amount)) ? 0 : Math.abs(toNumber(item.amount))
      };
      if (withCategory) {
        line.category = (item && item.category) || 'Other Expenses';
      }
      return line;
    });
  return {
    month: match ? parseInt(match[2], 10) : null,
    year: match ? parseInt(match[1], 10) : null,
    income: lines(source.income, false),
    expenses: lines(source.expenses, true),
    owner_distributions: lines(source.owner_distributions, false)
  };
}

/** Empty draft for manual entry (when the AI cannot read a statement). */
export function emptyReviewDraft(): ReviewDraft {
  return { month: null, year: new Date().getFullYear(), income: [], expenses: [], owner_distributions: [] };
}

/** Validation messages for the draft (empty array when valid). */
export function validateReviewDraft(draft: ReviewDraft): string[] {
  const errors: string[] = [];
  if (!draft.month || draft.month < 1 || draft.month > 12) {
    errors.push('Choose the statement month.');
  }
  if (!draft.year || draft.year < 2000 || draft.year > 2100) {
    errors.push('Enter the statement year (2000-2100).');
  }
  const check = (lines: ReviewLine[], label: string) => lines.forEach((line, i) => {
    const n = toNumber(line.amount);
    if (isNaN(n) || n < 0) {
      errors.push(`${label} line ${i + 1}: enter a positive amount.`);
    }
  });
  check(draft.income, 'Income');
  check(draft.expenses, 'Expense');
  check(draft.owner_distributions, 'Owner distribution');
  return errors;
}

/** Convert the draft to the API analysis shape (statement_date YYYY-MM-01). */
export function reviewDraftToAnalysis(draft: ReviewDraft): any {
  const mm = String(draft.month).padStart(2, '0');
  const amount = (line: ReviewLine) => Math.round(toNumber(line.amount) * 100) / 100;
  return {
    statement_date: `${draft.year}-${mm}-01`,
    income: draft.income.map(l => ({ description: l.description || 'Income', amount: amount(l) })),
    expenses: draft.expenses.map(l => ({ category: l.category || 'Other Expenses', description: l.description || '', amount: amount(l) })),
    owner_distributions: draft.owner_distributions.map(l => ({ description: l.description || 'Owner Distribution', amount: amount(l) }))
  };
}

export function sumLines(lines: ReviewLine[]): number {
  return Math.round((lines || []).reduce((t, l) => t + (isNaN(toNumber(l.amount)) ? 0 : toNumber(l.amount)), 0) * 100) / 100;
}

/** "2191 E Main Suite 1, March 2026 statement (file.pdf), uploaded 2026-10-04, applied" */
export function describeDuplicate(dup: any): string {
  if (!dup) {
    return '';
  }
  const parts: string[] = [];
  if (dup.propertyAddress) {
    parts.push(dup.propertyAddress);
  }
  if (dup.periodMonth) {
    const m = /^(\d{4})-(\d{2})$/.exec(dup.periodMonth);
    parts.push(m ? `${MONTH_NAMES[parseInt(m[2], 10) - 1]} ${m[1]} statement` : dup.periodMonth);
  }
  if (dup.originalFilename) {
    parts.push(`"${dup.originalFilename}"`);
  }
  if (dup.uploadedOn) {
    parts.push(`uploaded ${String(dup.uploadedOn).slice(0, 10)}`);
  }
  parts.push(dup.applied ? 'already applied' : 'not applied yet');
  return parts.join(', ');
}

/** Pull { code, message, duplicates } out of an HttpErrorResponse. */
export function apiError(error: any): { status: number, code: string | null, message: string, duplicates: any[] } {
  const body = (error && error.error) || {};
  return {
    status: (error && error.status) || 0,
    code: body.code || null,
    message: body.error || (error && error.message) || 'Unknown error',
    duplicates: Array.isArray(body.duplicates) ? body.duplicates : []
  };
}
